import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Button, Spacer, Text } from '@chakra-ui/react';

import MainModal from '../modal/MainModal';
import ComboboxField from '../forms/ComboboxField';
import useCommonToast from '../../hooks/useCommonToast';
import useExceptionToast from '../../hooks/useExceptionToast';
import { ISO_RTO_OPTIONS, isEmpty } from '../../services/utils';
import {
    getFirstLevelMarketModelItem,
    importShapesMIData,
    getFirstLevelShapeSetsItems,
    importVolsMIData,
} from '../../services/portfolios';
import { getIsoZoneHubList } from '../../services/traverseApi';

import ErrorAccordion from '../utils/ErrorAccordion';

import { THIRTY_SECOND_DURATION_IN_MILLISECONDS } from '../../constants/time';
import { FE_ONLY_FEATURE_TOGGLES } from 'constants/featureToggles';

const ImportMIDataToMainMarketModal = ({ onClose, source, closeImportMIDataModal, goToMarketModelItem }) => {
    const intl = useIntl();
    const { toast } = useCommonToast();
    const { exceptionToast } = useExceptionToast();

    const [isLoadingError, setIsLoadingError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedIsoRto, setSelectedIsoRto] = useState(null);
    const [selectedIsoHub, setSelectedIsoHub] = useState(null);
    const [selectedIsoHubsList, setSelectedIsoHubsList] = useState([]);

    const portfolioName = source.properties.name;
    const subNodes = source?.nodes;
    const modelItem = getFirstLevelMarketModelItem(subNodes);
    const shapeSetItems = getFirstLevelShapeSetsItems(subNodes);
    const mainMarketModelName = modelItem.name;

    const onImportMIShapesDataSuccess = (portfolioName, importResponse, mainMarketModelName) => {
        const release = importResponse.miRelease;
        const scenario = importResponse.miScenario;
        const ISO = importResponse.iso;
        const hubName = importResponse.hubName;

        let successDetails = [];
        successDetails.push('Portfolio : ' + portfolioName);
        successDetails.push('Market Price Model Item : ' + mainMarketModelName);
        successDetails.push('ISO : ' + ISO);
        successDetails.push('Hub : ' + hubName);
        successDetails.push('Release : ' + release);
        successDetails.push('Scenario : ' + scenario);

        for (let i = 0; i < importResponse?.importMIShapes.length; i++) {
            let shapeSetResponse = importResponse?.importMIShapes[i];
            let numberOfRecords = shapeSetResponse.dataRecordsCount;
            let shapeSetItemDescription = shapeSetResponse.shapeSetItemDescription;
            let errorMessage = '';

            //Check if there is an error message
            if (shapeSetResponse.errorType != null) {
                errorMessage = shapeSetResponse.errorMessage;
            }
            let message = numberOfRecords + ' records imported into ' + shapeSetItemDescription + '. ' + errorMessage;

            successDetails.push(message);
        }

        //Add a note to the Success message if there were errors in one or more Shape Set Items but not all.
        const errorResponses = importResponse?.importMIShapes?.filter((x) => x.success === false);
        const additionalMessage =
            errorResponses?.length > 0 ? 'Some shapes were not updated. See details for more info.' : '';

        toast({
            message: intl.formatMessage(
                { id: 'portfolios_import_mi_shapes_data_success_message' },
                {
                    portfolioName: portfolioName,
                    mainMarketModelItem: mainMarketModelName,
                    additionalMessage: additionalMessage,
                }
            ),
            slot: (
                <ErrorAccordion
                    status={'success'}
                    details={successDetails.map((str, index) => (
                        <React.Fragment key={index}>
                            {str}
                            {index < successDetails.length - 1 && <br />}
                        </React.Fragment>
                    ))}
                />
            ),
            duration: THIRTY_SECOND_DURATION_IN_MILLISECONDS,
        });
    };

    const onImportMIVolsDataSuccess = (portfolioName, importResponse, mainMarketModelName) => {
        const release = importResponse.miRelease;
        const scenario = importResponse.miScenario;
        const ISO = importResponse.iso;
        const hubName = importResponse.hubName;
        const numberOfRecords = importResponse.totalDataRecordsCount;
        const spotPriceDescription = importResponse?.spotPriceDescription;
        const spotPriceLookupValues = importResponse?.spotPriceLookupValues;

        let successDetails = [];
        successDetails.push('Portfolio : ' + portfolioName);
        successDetails.push('Market Price Model Item : ' + mainMarketModelName);
        spotPriceDescription && successDetails.push('Spot Price Item : ' + spotPriceDescription);
        spotPriceLookupValues && successDetails.push('Spot Price Lookup Values : ' + spotPriceLookupValues);
        successDetails.push('ISO : ' + ISO);
        successDetails.push('Hub : ' + hubName);
        successDetails.push('Release : ' + release);
        successDetails.push('Scenario : ' + scenario);
        const message = numberOfRecords + ' records imported into ' + spotPriceDescription + '. ';
        successDetails.push(message);

        if (Array.isArray(importResponse?.miPeakPeriodNames) && importResponse?.miPeakPeriodNames?.length) {
            successDetails.push('Peak Periods : ' + importResponse.miPeakPeriodNames.join(', '));
        }

        toast({
            message: intl.formatMessage(
                { id: 'portfolios_import_mi_vols_data_success_message' },
                {
                    portfolioName: portfolioName,
                    mainMarketModelItem: mainMarketModelName,
                }
            ),
            slot: (
                <ErrorAccordion
                    status={'success'}
                    details={successDetails.map((str, index) => (
                        <React.Fragment key={index}>
                            {str}
                            {index < successDetails.length - 1 && <br />}
                        </React.Fragment>
                    ))}
                />
            ),
            duration: THIRTY_SECOND_DURATION_IN_MILLISECONDS,
        });
    };

    const onImportMIVolsDataError = (portfolioName, importResponse, mainMarketModelName) => {
        const release = importResponse?.miRelease;
        const scenario = importResponse?.miScenario;
        const ISO = importResponse?.iso;
        const hubName = importResponse?.hubName;
        const spotPriceDescription = importResponse?.spotPriceDescription;
        const spotPriceLookupValues = importResponse?.spotPriceLookupValues;
        const errorMessage = importResponse?.errorMessage;

        let errorDetails = [];
        errorMessage && errorDetails.push('Error : ' + errorMessage);
        portfolioName && errorDetails.push('Portfolio : ' + portfolioName);
        mainMarketModelName && errorDetails.push('Market Price Model Item : ' + mainMarketModelName);
        spotPriceDescription && errorDetails.push('Spot Price Item : ' + spotPriceDescription);
        spotPriceLookupValues && errorDetails.push('Spot Price Lookup Values : ' + spotPriceLookupValues);
        ISO && errorDetails.push('ISO : ' + ISO);
        hubName && errorDetails.push('Hub : ' + hubName);
        release && errorDetails.push('Release : ' + release);
        scenario && errorDetails.push('Scenario : ' + scenario);

        const peakPeriodDetails =
            Array.isArray(importResponse?.miPeakPeriodNames) && importResponse?.miPeakPeriodNames?.length
                ? importResponse.miPeakPeriodNames.join(', ')
                : null;

        peakPeriodDetails &&
            errorDetails.push(
                intl.formatMessage({ id: 'portfolios_import_mi_vols_failed_peak_periods' }) + peakPeriodDetails
            );

        toast({
            status: 'error',
            message: errorMessage,
            slot: (
                <ErrorAccordion
                    status={'error'}
                    details={errorDetails.map((str, index) => (
                        <React.Fragment key={index}>
                            {str}
                            {index < errorDetails.length - 1 && <br />}
                        </React.Fragment>
                    ))}
                />
            ),
            duration: THIRTY_SECOND_DURATION_IN_MILLISECONDS,
        });
    };

    const getImportMIDataNoShapeSetMessage = (portfolioName) => {
        return intl.formatMessage({ id: 'portfolios_import_mi_data_no_shape_set_message' }, { portfolioName });
    };

    const onImportShapesMIData = async () => {
        const shapeSetItemIds = shapeSetItems?.map((a) => a.id);
        const modelItemId = modelItem?.id;

        if (shapeSetItems?.length > 0) {
            //Override Shapes if any sibling Shape Set Items are found
            const response = await importShapesMIData(selectedIsoRto, selectedIsoHub, modelItemId, shapeSetItemIds);
            const importResponses = response?.importMIShapes?.filter((x) => x.success === false);

            //Check for error message
            if (response?.errorMessage) {
                toast({
                    status: 'error',
                    message: response?.errorMessage,
                });
            } else if (importResponses?.length === response?.importMIShapes?.length) {
                //display the error messages for all the Shape Set Items and display them if found
                const errorDetails = importResponses?.map((a) => a.errorMessage);
                const combinedErrDetails = errorDetails.join(' ');
                const errorMessage = intl.formatMessage({ id: 'portfolios_import_mi_data_shapes_error' });

                exceptionToast(errorMessage, combinedErrDetails);
            } else {
                onImportMIShapesDataSuccess(portfolioName, response, mainMarketModelName);
            }
        } else {
            //If no sibling Shape Set Items were found in the selected portfolio
            toast(getImportMIDataNoShapeSetMessage(portfolioName));
        }
    };

    const onImportVolsMIData = async () => {
        const modelItemId = modelItem?.id;

        //Import volatitilies into Spot Volatility Forecast for the elec spot price in Main Market Model Item
        const response = await importVolsMIData(selectedIsoRto, selectedIsoHub, modelItemId);

        //Check for error message
        if (response?.errorMessage) {
            onImportMIVolsDataError(portfolioName, response, mainMarketModelName);
        } else {
            onImportMIVolsDataSuccess(portfolioName, response, mainMarketModelName);
        }
    };

    const onImportMIDataError = (error) => {
        if (error?.response?.status !== 401) {
            if (error?.response?.data?.error) {
                toast({
                    status: 'error',
                    message: error.response.data.error,
                });
            } else {
                toast({
                    status: 'error',
                    message: intl.formatMessage({ id: 'common_generic_saving_error' }),
                });
            }
            return;
        }
    };

    const importMIDataHandler = async () => {
        try {
            setIsSaving(true);
            //Import Shapes
            try {
                await onImportShapesMIData();
            } catch (error) {
                onImportMIDataError(error);
            }

            //Import Volatilities
            try {
                await onImportVolsMIData();
            } catch (error) {
                onImportMIDataError(error);
            }
        } finally {
            setIsSaving(false);
            closeImportMIDataModal();
            goToMarketModelItem(source);
        }
    };
    const onIsoRtoOptionChange = async (iso) => {
        setSelectedIsoRto(iso);
        setSelectedIsoHubsList([]);
        setSelectedIsoHub(null);
        setIsLoading(true);
        setIsLoadingError(false);

        try {
            const list = await getIsoZoneHubList(iso);
            setSelectedIsoHubsList(list);
        } catch (error) {
            setIsLoadingError(true);
            const errorMessage = intl.formatMessage({
                id: 'basis_configurator_iso_hub_list_loading_error',
            });
            toast({
                status: 'error',
                message: errorMessage,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <MainModal
            size="lg"
            scrollBehavior="inside"
            isOpen
            onClose={onClose}
            header={<FormattedMessage id="portfolios_import_mi_data_modal_title" values={{ portfolioName }} />}
            content={
                <>
                    <Box minHeight={350}>
                        <ComboboxField
                            id="isoRto"
                            name="isoRto"
                            label={intl.formatMessage({ id: 'basis_configurator_iso_rto' })}
                            options={ISO_RTO_OPTIONS}
                            valueKey="id"
                            labelKey="description"
                            value={selectedIsoRto}
                            onChange={(value) => onIsoRtoOptionChange(value)}
                            isRequired
                        />
                        <ComboboxField
                            mt={5}
                            id="isoHub"
                            name="isoHub"
                            label={intl.formatMessage({ id: 'portfolios_properties_zone_hub_label' })}
                            options={selectedIsoHubsList}
                            valueKey="description" // currently using description as key
                            labelKey="description"
                            value={selectedIsoHub}
                            onChange={(value) => setSelectedIsoHub(value)}
                            isRequired
                            isDisabled={isEmpty(selectedIsoHubsList)}
                            showPlaceholder={isLoading}
                            placeholderLabel={isLoading ? intl.formatMessage({ id: 'common_loading' }) : undefined}
                        />
                        <Text fontSize="sm" my={1} mt={25} mb={2}>
                            <FormattedMessage
                                id="portfolios_properties_import_mi_data_hint_text"
                                values={{ mainMarketModelName, portfolioName }}
                            />
                        </Text>
                        {FE_ONLY_FEATURE_TOGGLES.PS_FEATURE_TRAVERSE_VOLS_V2 && (
                            <Text
                                fontSize="sm"
                                my={1}
                                mt={2}
                                mb={25}
                                color="orange.700"
                                _dark={{ color: 'orange.500' }}
                            >
                                <FormattedMessage
                                    id="portfolios_properties_import_mi_volatilities_data_hint_text"
                                    values={{ mainMarketModelName, portfolioName }}
                                />
                            </Text>
                        )}
                    </Box>
                    <Spacer mb={25} />
                </>
            }
            secondaryButton={
                <Button variant="secondary" onClick={onClose} isDisabled={isSaving}>
                    <Box as="span" textTransform="capitalize">
                        <FormattedMessage id="common_close" />
                    </Box>
                </Button>
            }
            footerRightSlot={
                <Button
                    isLoading={isSaving}
                    isDisabled={!(selectedIsoRto && selectedIsoHub) || isLoadingError}
                    onClick={importMIDataHandler}
                    ml={3}
                >
                    <FormattedMessage id="portfolios_properties_import_mi_data_btn" />
                </Button>
            }
        />
    );
};

export default ImportMIDataToMainMarketModal;
